import { useEffect, useState } from "react"
import { doc, getDoc, setDoc } from "firebase/firestore"
import { httpsCallable } from "firebase/functions"
import { db, functions } from "../../firebase"
import { Box, Button, Chip, Paper, TextField, Typography, useTheme } from "@mui/material"
import SaveIcon from "@mui/icons-material/Save"
import LinkIcon from "@mui/icons-material/Link"
import RefreshIcon from "@mui/icons-material/Refresh"
import { toast } from "react-toastify"
import { tokens } from "../../theme"

interface IssuerSettings {
  qualifiedInvoiceNumber: string
}

interface FreeeAuthInfo {
  connected: boolean
  companyId?: number
  updatedAt?: string
  authorizeUrl: string
}

const freeeAuthInfo = httpsCallable<void, FreeeAuthInfo>(functions, 'freeeAuthInfo')

const CompanySettings = () => {
  const theme = useTheme()
  const colors = tokens(theme.palette.mode)
  const [qualifiedInvoiceNumber, setQualifiedInvoiceNumber] = useState("")
  const [saving, setSaving] = useState(false)
  const [freeeStatus, setFreeeStatus] = useState<FreeeAuthInfo | null>(null)
  const [loadingFreeeStatus, setLoadingFreeeStatus] = useState(false)

  const fetchFreeeStatus = async () => {
    setLoadingFreeeStatus(true)
    try {
      const result = await freeeAuthInfo()
      setFreeeStatus(result.data)
    } catch {
      toast.error("freee連携状態の取得に失敗しました")
    } finally {
      setLoadingFreeeStatus(false)
    }
  }

  useEffect(() => {
    getDoc(doc(db, "settings", "issuer")).then((snap) => {
      if (snap.exists()) {
        const data = snap.data() as IssuerSettings
        setQualifiedInvoiceNumber(data.qualifiedInvoiceNumber ?? "")
      }
    })
    fetchFreeeStatus()
  }, [])

  const handleSave = async () => {
    setSaving(true)
    try {
      await setDoc(doc(db, "settings", "issuer"), { qualifiedInvoiceNumber }, { merge: true })
      toast.success("保存しました")
    } catch {
      toast.error("保存に失敗しました")
    } finally {
      setSaving(false)
    }
  }

  return (
    <Box m={4}>
      <Typography variant="h4" color={colors.grey[100]} mb={3}>
        会社設定
      </Typography>
      <Box display="flex" flexDirection="column" gap={2} maxWidth={400}>
        <TextField
          label="適格事業者番号"
          placeholder="T1234567890123"
          value={qualifiedInvoiceNumber}
          onChange={(e) => setQualifiedInvoiceNumber(e.target.value)}
          helperText="インボイス制度の登録番号（例: T1234567890123）"
          inputProps={{ maxLength: 14 }}
        />
        <Box>
          <Button
            variant="contained"
            startIcon={<SaveIcon />}
            onClick={handleSave}
            disabled={saving}
          >
            保存
          </Button>
        </Box>
      </Box>

      <Typography variant="h4" color={colors.grey[100]} mt={5} mb={3}>
        freee連携
      </Typography>
      <Paper sx={{ p: 2, maxWidth: 480 }}>
        <Box display="flex" flexDirection="column" gap={2}>
          <Box display="flex" alignItems="center" gap={1}>
            <Typography variant="body1">連携状態:</Typography>
            {freeeStatus?.connected ? (
              <Chip label={`連携済み（事業所ID: ${freeeStatus.companyId}）`} color="success" size="small" />
            ) : (
              <Chip label="未連携" color="default" size="small" />
            )}
          </Box>
          {freeeStatus?.connected && freeeStatus.updatedAt && (
            <Typography variant="body2" color="text.secondary">
              最終連携日時: {new Date(freeeStatus.updatedAt).toLocaleString('ja-JP')}
            </Typography>
          )}
          <Typography variant="body2" color="text.secondary">
            出荷一覧から freee へ請求書を発行するには、freee との連携（認可）が必要です。下のボタンから freee の認可画面を開き、許可してください。
          </Typography>
          <Box display="flex" gap={1}>
            <Button
              variant="contained"
              color="info"
              startIcon={<LinkIcon />}
              disabled={!freeeStatus?.authorizeUrl}
              onClick={() => freeeStatus?.authorizeUrl && window.open(freeeStatus.authorizeUrl, '_blank', 'noopener,noreferrer')}
            >
              freeeと連携する
            </Button>
            <Button
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={fetchFreeeStatus}
              disabled={loadingFreeeStatus}
            >
              状態を更新
            </Button>
          </Box>
        </Box>
      </Paper>
    </Box>
  )
}

export default CompanySettings
